import { useEffect, useState } from "react";
import { Loader2, Save, Trash2, Plus, Upload, X, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { uploadImage } from "./AdminLogin";

export function Field({ label, children, wide = false }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="mb-1.5 block text-xs font-bold text-zinc-300">{label}</label>
      {children}
    </div>
  );
}

export function ImageField({ label, value, onChange }) {
  return (
    <Field label={label} wide>
      <div className="flex gap-3">
        <input className="field flex-1" dir="ltr" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`image-input-${label}`} />
        <label className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-2xl border border-white/15 px-4 text-xs font-bold text-gold hover:border-gold/50">
          <Upload size={14} /> رفع
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
            if (e.target.files?.[0]) onChange(await uploadImage(e.target.files[0]));
          }} />
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-3 h-24 w-44 rounded-xl border border-white/10 object-cover" />}
    </Field>
  );
}

export function ListField({ label, value, onChange, perLine = true }) {
  return (
    <Field label={perLine ? `${label} (كل عنصر في سطر)` : label} wide>
      <textarea className="field min-h-[110px]" value={Array.isArray(value) ? value.join("\n") : value || ""} onChange={(e) => onChange(e.target.value.split("\n").filter(Boolean))} />
    </Field>
  );
}

export default function CrudEditor({ name, title, fields, displayField = "title_ar", previewBase = null }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/list/${name}`);
      setItems(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [name]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing._doc_id) await api.put(`/admin/${name}/${editing._doc_id}`, editing);
      else await api.post(`/admin/${name}`, editing);
      setEditing(null);
      await load();
    } catch (e) {
      alert("فشل الحفظ: " + (e?.response?.data?.detail || "خطأ غير معروف"));
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    await api.delete(`/admin/${name}/${id}`);
    await load();
  };

  const setVal = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const docId = (it) => it._doc_id || it.id;

  return (
    <div data-testid={`admin-editor-${name}`}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        <button onClick={() => setEditing({ ...fields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? (f.type === "list" ? [] : "") }), {}) })} className="btn-gold !px-5 !py-2.5 text-sm" data-testid={`admin-add-${name}`}>
          <Plus size={16} /> إضافة
        </button>
      </div>

      {editing && (
        <div className="mb-8 rounded-3xl border border-gold/30 bg-coal/70 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold text-gold">{editing.id ? "تعديل عنصر" : "عنصر جديد"}</h3>
            <button onClick={() => setEditing(null)} className="text-mist hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => {
              const val = editing[f.key] ?? "";
              if (f.type === "image") return <ImageField key={f.key} label={f.label} value={val} onChange={(v) => setVal(f.key, v)} />;
              if (f.type === "list") return <ListField key={f.key} label={f.label} value={val} onChange={(v) => setVal(f.key, v)} />;
              if (f.type === "textarea") return (
                <Field key={f.key} label={f.label} wide={f.wide}>
                  <textarea className={`field ${f.big ? "min-h-[240px]" : "min-h-[90px]"}`} value={val} onChange={(e) => setVal(f.key, e.target.value)} dir={f.ltr ? "ltr" : "auto"} />
                </Field>
              );
              if (f.type === "select") return (
                <Field key={f.key} label={f.label}>
                  <select className="field cursor-pointer" value={val} onChange={(e) => setVal(f.key, e.target.value)}>
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              );
              return (
                <Field key={f.key} label={f.label}>
                  <input type={f.type === "number" ? "number" : "text"} className="field" value={val} onChange={(e) => setVal(f.key, e.target.value)} dir={f.ltr ? "ltr" : "auto"} />
                </Field>
              );
            })}
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} disabled={busy} className="btn-gold !px-6 !py-2.5 text-sm">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} حفظ
            </button>
            {previewBase && editing.slug && editing.status !== "draft" && (
              <a href={`${previewBase}/${editing.slug}`} target="_blank" rel="noopener" className="btn-ghost !px-5 !py-2.5 text-sm"><ExternalLink size={15} /> معاينة</a>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gold" /></div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-coal/50 p-4" data-testid={`admin-item-${name}-${it._doc_id || it.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                {it.image && <img src={it.image} alt="" className="h-10 w-14 shrink-0 rounded-lg object-cover" />}
                <span className="truncate text-sm font-semibold text-zinc-200">{it[displayField] || it.slug || it.id}</span>
                {it.status === "draft" && <span className="shrink-0 rounded-full bg-[#EF4444]/15 px-2 py-0.5 text-[10px] font-bold text-[#f87171]">مسودة</span>}
                {it.zone && <span className="shrink-0 rounded-full bg-gold/10 px-2 py-0.5 text-[10px] font-bold text-gold">{it.zone}</span>}
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => setEditing({ ...it })} className="rounded-xl border border-white/15 px-4 py-1.5 text-xs font-bold text-white hover:border-gold/50" data-testid={`admin-edit-${name}-${it._doc_id || it.id}`}>تعديل</button>
                <button onClick={() => remove(docId(it))} className="rounded-xl border border-[#EF4444]/40 px-3 py-1.5 text-xs font-bold text-[#f87171] hover:bg-[#EF4444]/10" data-testid={`admin-delete-${name}-${it._doc_id || it.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="rounded-2xl border border-white/10 bg-coal/50 p-6 text-center text-sm text-mist">لا توجد عناصر بعد</p>}
        </div>
      )}
    </div>
  );
}
